import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SwapHoriz as MigrationIcon, Download as DownloadIcon } from '@mui/icons-material';
import { usePlace } from '../../context/PlaceContext';
import {
  downloadMigrationCodesCsv,
  generateMigrationCodes,
  getMigrationStats,
  listMigrationCodes,
  regenerateMigrationCode,
  revokeMigrationCode,
  setMigrationCode,
} from '../../services/migrationService';
import { formatFecha } from '../../components/Migration/MigrationCodeContent';

const ESTADO_COLOR = {
  pendiente: 'info',
  activo: 'success',
  vencido: 'warning',
  revocado: 'error',
};

const detalleError = (error, porDefecto) =>
  (error && error.response && error.response.data && error.response.data.detail) || porDefecto;

const MigracionPage = () => {
  const { selectedPlace } = usePlace();
  const [placeId, setPlaceId] = useState(selectedPlace ? String(selectedPlace.id) : '');
  const [activatesAt, setActivatesAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  // Código mostrado una sola vez tras regenerar.
  const [codigoNuevo, setCodigoNuevo] = useState(null);
  // Diálogo para cargar un código emitido en ActionEV.
  const [cargar, setCargar] = useState(null); // { user_id, username, codigo }

  const placeIdNum = placeId ? Number(placeId) : null;

  const cargarDatos = useCallback(async () => {
    if (!placeIdNum) {
      setStats(null);
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [s, r] = await Promise.all([getMigrationStats(placeIdNum), listMigrationCodes(placeIdNum)]);
      setStats(s);
      setRows(r);
    } catch (e) {
      setError(detalleError(e, 'No se pudo cargar la información de migración.'));
    } finally {
      setLoading(false);
    }
  }, [placeIdNum]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const aviso = (texto) => {
    setMensaje(texto);
    setTimeout(() => setMensaje(''), 4000);
  };

  const ejecutar = async (fn, exito) => {
    setWorking(true);
    setError('');
    try {
      const r = await fn();
      if (exito) aviso(typeof exito === 'function' ? exito(r) : exito);
      await cargarDatos();
      return r;
    } catch (e) {
      setError(detalleError(e, 'La operación falló.'));
      return null;
    } finally {
      setWorking(false);
    }
  };

  const handleGenerar = () =>
    ejecutar(
      () => generateMigrationCodes(placeIdNum, activatesAt || null, expiresAt || null),
      (r) => `Códigos generados: ${r.generados}. Ya tenían código: ${r.existentes}.`
    );

  const handleExportar = (soloNoVistos) =>
    ejecutar(
      () => downloadMigrationCodesCsv(placeIdNum, soloNoVistos),
      (nombre) => `Descargado ${nombre}`
    );

  const handleRegenerar = async (row) => {
    if (!window.confirm(`¿Generar un código nuevo para ${row.username}? El anterior dejará de servir.`)) return;
    const r = await ejecutar(() => regenerateMigrationCode(row.user_id));
    if (r && r.codigo) setCodigoNuevo({ username: row.username, codigo: r.codigo });
  };

  const handleRevocar = async (row) => {
    if (!window.confirm(`¿Revocar el código de ${row.username}?`)) return;
    await ejecutar(() => revokeMigrationCode(row.user_id), 'Código revocado.');
  };

  const handleCargar = async () => {
    if (!cargar || !cargar.codigo) return;
    const r = await ejecutar(() => setMigrationCode(cargar.user_id, cargar.codigo), (x) => x.detail || 'Código cargado.');
    if (r) setCargar(null);
  };

  return (
    <Fade in timeout={600}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
              display: 'flex',
            }}
          >
            <MigrationIcon sx={{ color: '#FFFFFF', fontSize: '2rem' }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#002642', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Migración a ActionEV
            </Typography>
            <Typography variant="body1" sx={{ color: '#75787B' }}>
              Códigos con los que cada usuario vincula su cuenta en ActionEV
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {mensaje && <Alert severity="success" sx={{ mb: 2 }}>{mensaje}</Alert>}

        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#002642', fontWeight: 600, mb: 2 }}>
              Generar códigos
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="place_id (sede)"
                  type="number"
                  value={placeId}
                  onChange={(e) => setPlaceId(e.target.value)}
                  fullWidth
                  size="small"
                  helperText={selectedPlace ? `Sede actual: ${selectedPlace.name}` : ''}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Se activa el"
                  type="datetime-local"
                  value={activatesAt}
                  onChange={(e) => setActivatesAt(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText="Vacío: desde ya"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Vence el"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText="Vacío: sin vencimiento"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!placeIdNum || working}
                  onClick={handleGenerar}
                  sx={{ backgroundColor: '#002642', '&:hover': { backgroundColor: '#00577A' } }}
                >
                  Generar códigos
                </Button>
              </Grid>
            </Grid>
            <Typography variant="caption" sx={{ color: '#75787B', display: 'block', mt: 1 }}>
              Se crea un código por cada usuario de la sede que no sea administrador y no tenga ya uno.
              Los usuarios lo ven al entrar a COMHUB; aquí nunca se muestran.
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Generados: ${stats ? stats.generados : '-'}`} />
                <Chip color="success" variant="outlined" label={`Vistos: ${stats ? stats.vistos : '-'}`} />
                <Chip color="warning" variant="outlined" label={`No vistos: ${stats ? stats.no_vistos : '-'}`} />
                <Chip color="error" variant="outlined" label={`Revocados: ${stats ? stats.revocados : '-'}`} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={!placeIdNum || working}
                  onClick={() => handleExportar(false)}
                >
                  Exportar CSV (todos)
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={!placeIdNum || working}
                  onClick={() => handleExportar(true)}
                >
                  Solo no vistos
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        <Paper sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#002642' }} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Correo</TableCell>
                    <TableCell align="center">Versión</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="center">Vistas</TableCell>
                    <TableCell>Última vista</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ color: '#75787B', py: 4 }}>
                        {placeIdNum ? 'Esta sede no tiene códigos generados.' : 'Indica la sede.'}
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((row) => (
                    <TableRow key={row.user_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.username}</Typography>
                        <Typography variant="caption" sx={{ color: '#75787B' }}>{row.full_name}</Typography>
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell align="center">{row.version}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={row.estado} color={ESTADO_COLOR[row.estado] || 'default'} />
                      </TableCell>
                      <TableCell align="center">{row.shown_count}</TableCell>
                      <TableCell>{row.last_shown_at ? formatFecha(row.last_shown_at) : '-'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button size="small" disabled={working} onClick={() => handleRegenerar(row)}>
                            Regenerar
                          </Button>
                          <Button
                            size="small"
                            disabled={working}
                            onClick={() => setCargar({ user_id: row.user_id, username: row.username, codigo: '' })}
                          >
                            Cargar
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={working || row.status === 'revocado'}
                            onClick={() => handleRevocar(row)}
                          >
                            Revocar
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Dialog open={!!codigoNuevo} onClose={() => setCodigoNuevo(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Código nuevo para {codigoNuevo ? codigoNuevo.username : ''}</DialogTitle>
          <DialogContent dividers>
            <Typography
              sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '2rem', textAlign: 'center', letterSpacing: '0.12em', color: '#002642', userSelect: 'all' }}
            >
              {codigoNuevo ? codigoNuevo.codigo : ''}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Se muestra una sola vez. Cárgalo en ActionEV antes de cerrar; el usuario también lo verá al entrar a COMHUB.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCodigoNuevo(null)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!cargar} onClose={() => setCargar(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Cargar código para {cargar ? cargar.username : ''}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2, color: '#75787B' }}>
              Para cuando el código se regeneró en ActionEV y COMHUB debe mostrar el mismo.
            </Typography>
            <TextField
              label="Código (XXXXX-XXXXX)"
              value={cargar ? cargar.codigo : ''}
              onChange={(e) => setCargar((c) => ({ ...c, codigo: e.target.value.toUpperCase() }))}
              fullWidth
              autoFocus
              inputProps={{ style: { fontFamily: 'monospace', letterSpacing: '0.1em' } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCargar(null)}>Cancelar</Button>
            <Button variant="contained" disabled={working || !cargar || !cargar.codigo} onClick={handleCargar}>
              Cargar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Fade>
  );
};

export default MigracionPage;
